import { useRef, useState } from 'react';
import type { AssignmentId, ExpectedProgressStatus, TaskId, TaskProgressMode } from '@bigint-project/core';

import { ProgressStatusBadge } from '../progress/index.js';
import { formatHours } from '../resource/format.js';

/** 一位資源在某一週的一列（= 一筆指派）。 */
export interface TimesheetGridRow {
  readonly assignmentId: AssignmentId;
  readonly taskId: TaskId;
  readonly wbs: string;
  readonly taskName: string;
  readonly progressMode: TaskProgressMode;
  readonly status: ExpectedProgressStatus;
  readonly expectedPercent: number;
  readonly percentComplete: number;
  /** 該指派的累計實際工時（小時）。 */
  readonly totalActualHours: number;
  /** 該指派的剩餘工時（小時）。 */
  readonly remainingHours: number;
  /** 只含本週七天，順序與 `dates` 一致。 */
  readonly cells: readonly { readonly date: string; readonly hours: number }[];
}

export interface TimesheetGridProps {
  /** 本週日期（`YYYY-MM-DD`，七天）。 */
  readonly dates: readonly string[];
  /** 非工作日（淡化但仍可填）。 */
  readonly nonWorkingDates: ReadonlySet<string>;
  readonly rows: readonly TimesheetGridRow[];
  readonly readOnly: boolean;
  /** 單格提交（blur／Enter）；由呼叫端負責整週送出。 */
  readonly onCellCommit: (assignmentId: AssignmentId, date: string, hours: number) => void;
  readonly onOpenTaskProgress: (taskId: TaskId) => void;
}

const MODE_LABEL: Record<TaskProgressMode, string> = {
  work: '依工時',
  manual: '指定進度',
};

/**
 * 資源逐日工時網格：列 = 該資源的指派，欄 = 一週七天。
 *
 * 純展示元件（props in / callbacks out）。輸入在 blur／Enter 時提交單一格子；
 * 「整週一起送」是呼叫端的責任 —— 元件不持有專案資料，湊不出整週的 entries。
 */
export function TimesheetGrid({
  dates,
  nonWorkingDates,
  rows,
  readOnly,
  onCellCommit,
  onOpenTaskProgress,
}: TimesheetGridProps): React.JSX.Element {
  const [editing, setEditing] = useState<{ assignmentId: AssignmentId; date: string; draft: string } | null>(null);
  // 與 cost/Timesheet 相同的重複提交防護：Enter 送出後 input 從樹上移除，
  // 瀏覽器仍會補發一次 blur，沿用同一個 commit closure。
  const committedRef = useRef(false);

  const startEditing = (assignmentId: AssignmentId, date: string, hours: number): void => {
    if (readOnly) return;
    committedRef.current = false;
    setEditing({ assignmentId, date, draft: hours === 0 ? '' : String(hours) });
  };

  const commit = (): void => {
    if (committedRef.current || editing === null) return;
    committedRef.current = true;
    const parsed = editing.draft.trim() === '' ? 0 : Number(editing.draft);
    if (Number.isFinite(parsed) && parsed >= 0) onCellCommit(editing.assignmentId, editing.date, parsed);
    setEditing(null);
  };

  return (
    <div data-testid="timesheet-grid" className="flex h-full w-full flex-col overflow-auto bg-surface text-sm text-fg">
      <div className="flex border-b border-line-strong bg-raised font-medium">
        <div className="w-64 shrink-0 border-r border-line px-2 py-1">任務</div>
        {dates.map((date) => (
          <div key={date} className="w-16 shrink-0 border-r border-line px-1 py-1 text-center">
            {date.slice(5)}
          </div>
        ))}
        <div className="w-20 shrink-0 border-r border-line px-2 py-1 text-right">週合計</div>
        <div className="w-20 shrink-0 border-r border-line px-2 py-1 text-right">累計實際</div>
        <div className="w-20 shrink-0 border-r border-line px-2 py-1 text-right">剩餘</div>
        <div className="w-16 shrink-0 border-r border-line px-2 py-1 text-right">完成%</div>
        <div className="w-40 shrink-0 px-2 py-1">狀態</div>
      </div>

      {rows.map((row) => {
        const weekTotal = row.cells.reduce((sum, cell) => sum + cell.hours, 0);
        return (
          <div
            key={row.assignmentId as string}
            role="row"
            data-testid={`timesheet-grid-row-${row.assignmentId}`}
            className="flex border-b border-hairline"
          >
            <div className="w-64 shrink-0 truncate border-r border-hairline px-2 py-1" title={`${row.wbs} ${row.taskName}`}>
              <span className="text-fg-muted">{row.wbs}</span> {row.taskName}
            </div>
            {dates.map((date) => {
              const cell = row.cells.find((candidate) => candidate.date === date);
              const hours = cell?.hours ?? 0;
              const isEditing = editing?.assignmentId === row.assignmentId && editing.date === date;
              return (
                <div
                  key={date}
                  data-testid={`timesheet-grid-cell-${row.assignmentId}-${date}`}
                  className={[
                    'w-16 shrink-0 border-r border-hairline px-1 py-1 text-right',
                    nonWorkingDates.has(date) ? 'bg-sunken text-fg-subtle' : '',
                  ].join(' ')}
                  onDoubleClick={() => startEditing(row.assignmentId, date, hours)}
                >
                  {isEditing ? (
                    <input
                      autoFocus
                      aria-label={`${row.taskName} ${date} 的實際工時（小時）`}
                      type="number"
                      min="0"
                      step="any"
                      className="w-full bg-transparent text-right outline-none"
                      value={editing.draft}
                      onChange={(event) => setEditing({ ...editing, draft: event.target.value })}
                      onBlur={commit}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') commit();
                        if (event.key === 'Escape') {
                          committedRef.current = true;
                          setEditing(null);
                        }
                      }}
                    />
                  ) : (
                    formatHours(hours)
                  )}
                </div>
              );
            })}
            <div data-testid={`timesheet-grid-week-${row.assignmentId}`} className="w-20 shrink-0 border-r border-hairline px-2 py-1 text-right">
              {formatHours(weekTotal)}
            </div>
            <div className="w-20 shrink-0 border-r border-hairline px-2 py-1 text-right">{formatHours(row.totalActualHours)}</div>
            <div className="w-20 shrink-0 border-r border-hairline px-2 py-1 text-right">{formatHours(row.remainingHours)}</div>
            <div className="w-16 shrink-0 border-r border-hairline px-2 py-1 text-right">{Math.round(row.percentComplete)}%</div>
            <div className="flex w-40 shrink-0 items-center gap-2 px-2 py-1">
              <ProgressStatusBadge
                status={row.status}
                title={`預期進度 ${Math.round(row.expectedPercent)}%`}
                testId={`timesheet-grid-status-${row.assignmentId}`}
              />
              <span className="text-xs text-fg-subtle">{MODE_LABEL[row.progressMode]}</span>
              <button
                type="button"
                aria-label={`開啟 ${row.taskName} 的任務進度`}
                data-testid={`timesheet-grid-open-${row.assignmentId}`}
                className="ml-auto rounded px-1 text-fg-muted hover:bg-raised"
                onClick={() => onOpenTaskProgress(row.taskId)}
              >
                …
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
